import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTemplateStore } from "@/zustand/templateStore";
import CreditCards from "@/components/Misc/creditCards";
import s from "@/components/Accordion/Accordion.module.scss";
import formStyles from "@/components/Forms/Form.module.scss";
import { cn } from "@/utils/classNames.js";

const Accordion = ({ isLast, children, label, image, name, overflow, maxImgWidth, imageHeight }) => {
  const { paymentMethod, handlePaymentMethodChange, enhancedContrast: isEnhancedContrast } = useTemplateStore();
  const [expanded, setExpanded] = useState(false);
  const [overflowSetting, setOverflowSetting] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  const contentRef = useRef(null);
  const innerContentRef = useRef(null);
  const [currentMaxHeight, setCurrentMaxHeight] = useState(0);

  const cardButtonColor = window?.post_template?.checkout_v6_products?.checkout_v6_products_checkButtonCcColor;
  const showBorderCC = window?.post_template?.checkout_v6_products?.checkout_v6_products_borderCC;
  const headingBgColor = window?.post_template?.checkout_v6_products?.checkout_v6_products_paymentHeadingBgColor;

  if (headingBgColor) {
    document.documentElement.style.setProperty("--payment-heading-bg-color", headingBgColor);
  }
  if (cardButtonColor) {
    document.documentElement.style.setProperty("--checkbutton-cc-color", cardButtonColor);
  }

  useEffect(() => {
    let timer;
    if (overflow) {
      timer = setTimeout(() => setOverflowSetting(true), 600);
    } else {
      setOverflowSetting(false);
    }
    return () => clearTimeout(timer);
  }, [overflow]);

  useEffect(() => setIsInitialRender(false), []);

  useEffect(() => {
    setExpanded(name === paymentMethod);
  }, [paymentMethod, name]);

  const updateHeight = useCallback(() => {
    if (expanded && innerContentRef.current && contentRef.current) {
      const originalStyle = {
        overflow: contentRef.current.style.overflow,
        maxHeight: contentRef.current.style.maxHeight,
        visibility: contentRef.current.style.visibility,
      };

      contentRef.current.style.visibility = "hidden";
      contentRef.current.style.overflow = "visible";
      contentRef.current.style.maxHeight = "none";

      const _ = innerContentRef.current.offsetHeight;
      setCurrentMaxHeight(innerContentRef.current.scrollHeight + 64);

      Object.assign(contentRef.current.style, originalStyle);
    } else {
      setCurrentMaxHeight(0);
    }
  }, [expanded]);

  useEffect(() => {
    const element = innerContentRef.current;
    if (!element) return;

    if (expanded) {
      requestAnimationFrame(updateHeight);
    } else {
      setCurrentMaxHeight(0);
    }

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateHeight);
    });

    resizeObserver.observe(element);
    return () => {
      resizeObserver.unobserve(element);
      resizeObserver.disconnect();
    };
  }, [expanded, updateHeight]);

  const handleAccordionClick = (e) => {
    e.preventDefault();
    handlePaymentMethodChange(name);
  };

  const wrapperStyle = {
    transition: isInitialRender && expanded ? "none" : undefined,
    overflow: overflowSetting ? "visible" : "hidden",
    maxHeight: `${currentMaxHeight}px`,
  };

  return (
    <>
      <div
        onClick={handleAccordionClick}
        expanded={expanded.toString()}
        className={cn(
          showBorderCC ? s["accordionContainer__heading--border"] : s.accordionContainer__heading,
          !expanded && s["accordionContainer__heading--unchecked"],
          isEnhancedContrast && s.enhancedContrast,
          !expanded && !isLast && s["accordionContainer__heading--middle"]
        )}
      >
        <button
          className={cn(s.accordionContainer__button, isEnhancedContrast && s.enhancedContrast)}
          type="button"
        ></button>

        <div className={s.textLabelWrapper}>
          {label && (
            <span className={cn(formStyles.cardRadio__labelText, isEnhancedContrast && formStyles.enhancedContrast)}>
              {label}
            </span>
          )}

          {name === "card" ? (
            <CreditCards />
          ) : (
            <div className={cn(s.cardsContainer, name)}>
              <img
                width="85"
                height="24"
                className={formStyles.cardRadio__image}
                src={image}
                alt="Payments"
                style={{
                  maxWidth: `${maxImgWidth}px`,
                  marginLeft: label ? "auto" : 0,
                  height: imageHeight && `${imageHeight}px`,
                }}
              />
            </div>
          )}
        </div>
      </div>
      <section className={s.accordionContainer__wrapper} style={wrapperStyle} aria-expanded={expanded} ref={contentRef}>
        <div
          ref={innerContentRef}
          className={cn(
            s.accordionContainer__content,
            name !== "card" && s["accordionContainer__content--default"],
            isEnhancedContrast && s.enhancedContrast,
            isLast && s["accordionContainer__content--last"]
          )}
        >
          {children}
        </div>
      </section>
    </>
  );
};

export default Accordion;
